import { ModalType, SvgType } from 'src/types/types'
import { RootState } from 'src/store/store'
import { MouseEvent, RefObject, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { setModalItems } from 'src/store/Reducer/common/modalSlice'
import {
  setSelectPin,
  setSoundListFlag,
  setEmptyComponentStatus,
  setSoundPlayStatus,
} from 'src/store/Reducer/page/locationPageSlice'
import { useAppDispatch } from 'src/hooks/redux/useAppDispatch'
import { useAppSelector } from 'src/hooks/redux/useAppSelector'
import LocationTemplate from 'src/components/atomic/template/Service/Location'
import useLocationEventBus from 'src/hooks/useEventBus/useLocationEventBus'
import useMediaQueryHook from 'src/hooks/useMediaQuery.hook'
import { useBeforeUnload, useNavigate } from 'react-router-dom'
import { Cmdtypes } from 'src/constants/cmdtypes'
import { commonUtils } from 'src/utils/common.utils'
import { CryptoUtils } from 'src/utils/Crypto.utils'

import { RequestDataBody, SvcLocationType } from 'src/types/websocket'
import { SocketSender } from 'src/api/socket'

export type LocationDataPropsType = {
  convertData: (data: SvcLocationType[]) => { date: string; filterData: SvcLocationType[] }[]
  getLocationTotalData: () => void
  modalCall: (type: ModalType) => void
  playSoundList: string[]
  onSoundBtnClick: (e: MouseEvent<HTMLButtonElement>) => void
  onSoundListItemClick: (e: MouseEvent<HTMLSpanElement>) => void
  onLocationHistoryClick: (fData: SvcLocationType) => void
  OptionRef: RefObject<HTMLDivElement> | null
  locationTypeText: { [key: number]: { text: string; svgName: SvgType } }
}
const LocationPage = () => {
  const navigate = useNavigate()
  const { currentMedia } = useMediaQueryHook()
  const { t } = useTranslation()
  const soundArray = t('MODAL.PLAY_SOUND.LIST', {
    defaultValue: [
      'Notifying call from device owner',
      'Lost phone alert',
      'Declare warning',
      'Real-time phone location',
      'Reward for returning lost device',
      'Police siren',
    ],
    returnObjects: true,
  })
  const playSoundList = Object.values(soundArray)
  const dispatch = useAppDispatch()
  const OptionRef = useRef<HTMLDivElement>(null)
  const deviceAuth = useAppSelector((state: RootState) => state.deviceAuth)
  const { Connection } = useAppSelector((state: RootState) => state.connection)

  const { LocationInfos } = useAppSelector((state: RootState) => state.locationHistory)
  const { soundPlayStatus, soundListFlag } = useAppSelector(
    (state: RootState) => state.LocationPage,
  )
  const { setDateFormat } = commonUtils

  const locationTypeText: { [key: number]: { text: string; svgName: SvgType } } = {
    0: {
      text: t('LOCATE.HISTORY.LOCK_FIRST_TIME', {
        defaultValue: 'Device Locked for the first time',
      }) as string,
      svgName: 'location_first_lock',
    },
    1: {
      text: t('LOCATE.HISTORY.DEVICE_LOCATED', { defaultValue: 'Device Located' }) as string,
      svgName: 'location_device_lock',
    },
    2: {
      text: t('LOCATE.HISTORY.TURN_OFF', {
        defaultValue: 'Detect a physical button press',
      }) as string,
      svgName: 'location_turn_off_attempt',
    },
  }
  const onLocationHistoryClick = (fData: SvcLocationType) => {
    dispatch(setSelectPin(fData))
    if (currentMedia !== 'pc') {
      navigate(`/location/detail/${fData.Idx}`)
    }
  }

  const onSoundListItemClick = (e: MouseEvent<HTMLSpanElement>) => {
    // 사이렌 재생 요청 - 3005
    const target = e.target as HTMLElement

    playSoundList.forEach((d, idx) => {
      if (target.innerText === d) {
        dispatch(SocketSender<RequestDataBody.PlaySound>(Cmdtypes.SIREN_START, { Type: idx }))
        dispatch(setSoundPlayStatus(true))
        dispatch(setSoundListFlag(false))
      }
    })
  }

  const onSoundBtnClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (!soundPlayStatus) {
      dispatch(setSoundListFlag(!soundListFlag))
    } else {
      dispatch(SocketSender(Cmdtypes.SIREN_STOP))
      dispatch(setSoundPlayStatus(false))
    }
  }

  const modalCall = (type: ModalType) => {
    dispatch(SocketSender(Cmdtypes.WIFI_LIST))
    dispatch(
      setModalItems({
        type: type,
        text: '',
      }),
    )
  }

  const convertData = (data: SvcLocationType[]) => {
    const result: Array<{ date: string; filterData: SvcLocationType[] }> = []
    data?.forEach((data) => {
      const date = setDateFormat('LOCATION_DATE', data.Date as number) //data.Date.split(' ')[0]
      const findData = result.find((entry) => entry.date === date)
      findData ? findData.filterData.push(data) : result.push({ date, filterData: [data] })
    })
    return result
  }

  const getLocationTotalData = useCallback(() => {
    if (CryptoUtils.decryptionVerify()) {
      Connection && dispatch(SocketSender(Cmdtypes.CURRENT_LOCATION_INFO))
      dispatch(SocketSender(Cmdtypes.LOCATION_INFO))
    }
  }, [dispatch, Connection])

  useLocationEventBus()

  useEffect(() => {
    switch (true) {
      case !Connection && LocationInfos?.length === 0:
        dispatch(setEmptyComponentStatus('LocationNoData'))
        break
      case Connection && !deviceAuth.Location:
        dispatch(setEmptyComponentStatus('LocationNoAuth'))
        break
      default:
        dispatch(setEmptyComponentStatus('default'))
        break
    }

    if (!Connection && soundPlayStatus) {
      dispatch(
        setModalItems({
          type: 'alert',
          text: `${t('MODAL.PLAY_SOUND.OFFLINE', {
            defaultValue: 'Unable to play sound because your phone is offline.',
          })}`,
        }),
      )
    }
  }, [t, Connection, deviceAuth.Location, LocationInfos, soundPlayStatus, dispatch])

  useEffect(() => {
    const handleOutsideClose = (e: { target: any }) => {
      // PlaySound 버튼 클릭시 엘리먼트 바깥을 클릭 시 드롭메뉴 닫힘
      if (soundListFlag && !OptionRef?.current?.contains(e.target))
        dispatch(setSoundListFlag(false))
    }
    document.addEventListener('click', handleOutsideClose)
    return () => {
      document.removeEventListener('click', handleOutsideClose)
    }
  }, [soundListFlag, dispatch])

  useBeforeUnload(useCallback((e) => dispatch(SocketSender(Cmdtypes.SIREN_STOP)), [dispatch]))

  const data: LocationDataPropsType = {
    OptionRef,
    playSoundList,
    modalCall,
    convertData,
    onSoundBtnClick,
    getLocationTotalData,
    onSoundListItemClick,
    onLocationHistoryClick,
    locationTypeText,
  }
  return <LocationTemplate data={data} />
}
export default LocationPage
